'use client';

import { cn } from '@/lib/utils';
import { forwardRef, JSXElementConstructor, ReactElement, ReactNodeArray, useState } from 'react';
import { UseFormRegister } from 'react-hook-form';

interface RadioButtonProps {
    options: { label: string | ReactElement<any, string | JSXElementConstructor<any>> | ReactNodeArray; value: string }[];
    // eslint-disable-next-line no-unused-vars
    onChange?: (option: string) => void;
    register?: UseFormRegister<any>;
    className?: string;
    label?: string;
    required?: boolean;
    disabled?: boolean;
    value: string;
}

const Radio = forwardRef<HTMLInputElement, RadioButtonProps>((props, ref) => {
    const {
        options,
        onChange,
        register,
        disabled,
        label,
        required,
        className,
        value,
        ...inputProps
    } = props;

    const [selectedValue, setSelectedValue] = useState<string>(value ?? options[0]?.value);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSelectedValue(value);
        if (onChange) onChange(e.target.value);
    };

    return (
        <div className={cn('flex w-full items-center flex-wrap', className)}>
            {label && (
                <p className="text-h6 font-medium text-black mr-12 inline-block">
                    {label}
                    {required && '*'}
                </p>
            )}
            {options.map((option) => (
                <label
                    key={option.value}
                    className={cn(
                        'inline-flex w-fit mr-7 cursor-pointer items-center max-lg:mt-[10px]',
                        disabled && 'pointer-events-none cursor-default',
                    )}
                >
                    <input
                        {...register}
                        {...inputProps}
                        ref={ref}
                        type="radio"
                        value={option.value}
                        checked={option.value === selectedValue}
                        onChange={handleChange}
                        className="form-radio h-4 min-h-4 w-4 min-w-4"
                        disabled={disabled}
                    />
                    <span className="ml-4 font-normal text-p">{option.label}</span>
                </label>
            ))}
        </div>
    );
});

Radio.displayName = 'Radio';

export default Radio;
